import { getLang, localeOf, plural } from './i18n';
import { addDaysToKey, localDays } from './local-days';

export { addDaysToKey };

/**
 * All times from the API are UTC ISO strings. Everything displayed is in the
 * coach's timezone, not the device's — a client booking from London must see
 * "08:00", the time they'll actually be on court in Limassol, not "06:00".
 * That's the one case where using the device timezone would be actively wrong.
 */

export const COACH_TZ = import.meta.env.VITE_COACH_TZ ?? 'Asia/Nicosia';

/**
 * The timezone is the coach's; the LANGUAGE is the reader's.
 *
 * These read the current language from the store rather than taking it
 * as an argument, because they are called from a hundred places that
 * have no other reason to know about language. Every screen that renders
 * a date also renders translated text, so it is already subscribed and
 * re-renders when the switch is tapped.
 *
 * Formatters are built per call instead of once at module load — Intl
 * caches internally, and a formatter frozen at import time would keep
 * showing English after a switch.
 */
const fmt = (o: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat(localeOf(getLang()), { timeZone: COACH_TZ, ...o });

export const fmtTime = (iso: string | Date) =>
  fmt({ hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso));
export const fmtWeekday = (d: Date) => fmt({ weekday: 'short' }).format(d);
export const fmtDayNum = (d: Date) => fmt({ day: 'numeric' }).format(d);
/** "Sep" / "сент." */
export const fmtMonthShort = (d: Date) => fmt({ month: 'short' }).format(d);
/** "Saturday, 26 September" / "суббота, 26 сентября" */
export const fmtLongDay = (d: Date) =>
  fmt({ weekday: 'long', day: 'numeric', month: 'long' }).format(d);
/** "Sat 26 Sept" / "сб, 26 сент." — for a timeline's left column. */
export const fmtShortDay = (d: Date) =>
  fmt({ weekday: 'short', day: 'numeric', month: 'short' }).format(d);

/** 'YYYY-MM-DD' for a Date, evaluated in the coach's timezone. */
export function toCoachDateKey(d: Date): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: COACH_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)!.value;
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Hour of day (0-23) in the coach's timezone. */
export function coachHour(iso: string | Date): number {
  return Number(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: COACH_TZ,
      hour: '2-digit',
      hour12: false,
    }).format(new Date(iso)),
  );
}

/** N consecutive days starting today: calendar days in the coach's timezone. */
export function nextDays(count: number): Date[] {
  return localDays(count, COACH_TZ);
}

/**
 * Every day from one key to another, inclusive. Each is noon UTC, which
 * falls on the same date in the coach's timezone anywhere within
 * eleven hours of UTC.
 */
export function dayRange(fromKey: string, toKey: string): Date[] {
  const out: Date[] = [];
  for (let k = fromKey; k <= toKey; k = addDaysToKey(k, 1)) out.push(new Date(`${k}T12:00:00Z`));
  return out;
}

export function isToday(d: Date): boolean {
  return toCoachDateKey(d) === toCoachDateKey(new Date());
}

/**
 * How long ago something happened: "3 days", "5 h" / "3 дня", "5 ч".
 *
 * relativeTo below counts down to a deadline and answers "passed" for
 * anything already behind us, which is right there and nonsense here —
 * "Last trained passed ago" is what every client row used to say.
 */
export function elapsedSince(iso: string): string {
  const lang = getLang();
  const ms = Date.now() - new Date(iso).getTime();
  if (ms <= 0) return lang === 'ru' ? 'только что' : 'just now';
  const hours = ms / 3_600_000;
  if (hours < 1) {
    const min = Math.max(1, Math.round(ms / 60_000));
    return `${min} ${lang === 'ru' ? 'мин' : 'min'}`;
  }
  if (hours < 24) {
    const h = Math.round(hours);
    return `${h} ${lang === 'ru' ? 'ч' : 'h'}`;
  }
  const days = Math.round(hours / 24);
  return `${days} ${plural(lang, days, lang === 'ru' ? ['день', 'дня', 'дней'] : ['day', 'days'])}`;
}

/** "3 h", "5 days" / "3 ч", "5 дней" — for cancellation deadlines. */
export function relativeTo(iso: string): string {
  const lang = getLang();
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return lang === 'ru' ? 'прошло' : 'passed';
  const hours = ms / 3_600_000;
  if (hours < 1) {
    const min = Math.round(ms / 60_000);
    return `${min} ${lang === 'ru' ? 'мин' : 'min'}`;
  }
  if (hours < 24) {
    const h = Math.round(hours);
    return `${h} ${lang === 'ru' ? 'ч' : 'h'}`;
  }
  const days = Math.round(hours / 24);
  return `${days} ${plural(lang, days, lang === 'ru'
    ? ['день', 'дня', 'дней']
    : ['day', 'days'])}`;
}
